// src/pages/Usuarios.tsx - ✅ VERSIÓN CORREGIDA

import { useState, useEffect } from 'react'
import { useTheme } from '../contexts/ThemeContext'
import { useAuthStore } from '../stores/authStore'
import { 
  Users, UserPlus, Edit, Trash2, Search, Filter, 
  Download, ChevronLeft, ChevronRight, Crown, CheckCircle, 
  XCircle, X, Save, RefreshCw, SortAsc, SortDesc, Key  
} from 'lucide-react'
import { adminUserService, adminExportService } from '../services/adminApiService'

export default function Usuarios() {
  const { isDark } = useTheme()
  const { user: currentUser } = useAuthStore()
  
  const [users, setUsers] = useState([])
  const [loading, setLoading] = useState(true)
  const [showUserModal, setShowUserModal] = useState(false)
  const [editingUser, setEditingUser] = useState(null)
  const [selectedUsers, setSelectedUsers] = useState(new Set())
  const [searchQuery, setSearchQuery] = useState('')
  
  // ✅ FIX: Usar 'status' para UI, se mapea a 'subscription_status' en backend
  const [filters, setFilters] = useState({ 
    status: 'all', 
    role: 'all' 
  })
  
  const [sort, setSort] = useState({ column: 'created_at', direction: 'desc' })
  const [pagination, setPagination] = useState({
    currentPage: 1,
    itemsPerPage: 10,
    totalItems: 0,
    totalPages: 0
  })

  useEffect(() => {
    loadUsers()
  }, [pagination.currentPage, filters, sort])

  const loadUsers = async () => {
    try {
      setLoading(true)
      
      // ✅ Validación: Asegurar que el backend recibe parámetros correctos
      const params = {
        page: pagination.currentPage,
        limit: pagination.itemsPerPage,
        sortBy: sort.column,
        sortOrder: sort.direction,
        // ✅ Filtrar solo valores que no sean 'all'
        ...(filters.status !== 'all' && { status: filters.status }),
        ...(filters.role !== 'all' && { role: filters.role }),
        ...(searchQuery && { search: searchQuery })
      }

      console.log('📊 [USUARIOS] Cargando usuarios con params:', params)

      const response = await adminUserService.getUsers(params)
      
      // ✅ Validación: Verificar estructura de respuesta
      if (!response || !response.users) {
        console.error('❌ [USUARIOS] Respuesta inválida:', response)
        throw new Error('Estructura de respuesta inválida')
      }

      setUsers(response.users || [])
      setPagination(prev => ({
        ...prev,
        totalItems: response.pagination?.totalItems || 0,
        totalPages: response.pagination?.totalPages || 0
      }))
      
      console.log('✅ [USUARIOS] Usuarios cargados:', response.users.length)
      
    } catch (error) {
      console.error('❌ [USUARIOS] Error loading users:', error)
      setUsers([])
      // TODO: Agregar notificación toast
    } finally {
      setLoading(false)
    }
  }
  
  const [showPasswordModal, setShowPasswordModal] = useState(false)
  const [resetPasswordUser, setResetPasswordUser] = useState(null)
  const [newPassword, setNewPassword] = useState('')

  const handleResetPassword = async () => {
    if (!resetPasswordUser || !newPassword) return
    
    try {
      await adminUserService.resetPassword(resetPasswordUser.id, newPassword)
      setShowPasswordModal(false)
      setResetPasswordUser(null)
      setNewPassword('')
      // TODO: Mostrar toast de éxito
    } catch (error) {
      console.error('Error resetting password:', error)
      // TODO: Mostrar toast de error
    }
  }

  const handleUserAction = async (action, userId, data) => {
    try {
      if (action === 'create') await adminUserService.createUser(data)
      else if (action === 'update') await adminUserService.updateUser(userId, data)
      else if (action === 'delete') await adminUserService.deleteUser(userId)
      
      await loadUsers()
      setShowUserModal(false)
      setEditingUser(null)
    } catch (error) {
      console.error(`Error ${action} user:`, error)
    }
  }

  const handleExport = async (format) => {
    try {
      await adminExportService.exportUsers(format, filters)
    } catch (error) {
      console.error('Error exporting:', error)
    }
  }

  const cardClass = isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
  const textClass = isDark ? 'text-white' : 'text-gray-900'
  const mutedTextClass = isDark ? 'text-gray-400' : 'text-gray-600'
  const inputClass = isDark ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'

  return (
    <div className={`min-h-screen ${isDark ? 'bg-gray-900' : 'bg-gray-50'} p-6`}>
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className={`text-3xl font-bold ${textClass} mb-2`}>
              Gestión de Usuarios
            </h1>
            <p className={`${mutedTextClass}`}>
              Administra los usuarios del sistema ({users.length} usuarios)
            </p>
          </div>

          <div className="flex gap-3">
            <select
              onChange={(e) => handleExport(e.target.value)}
              className={`px-4 py-2 rounded-lg border ${inputClass}`}
            >
              <option value="">Exportar</option>
              <option value="csv">CSV</option>
              <option value="excel">Excel</option>
              <option value="pdf">PDF</option>
            </select>

            <button
              onClick={() => {
                setEditingUser(null)
                setShowUserModal(true)
              }}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg"
            >
              <UserPlus className="w-4 h-4" />
              Nuevo Usuario
            </button>
          </div>
        </div>

        {/* Filters */}
        <div className="flex gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Buscar usuarios..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && loadUsers()}
              className={`w-full pl-10 pr-4 py-3 rounded-lg border ${inputClass}`}
            />
          </div>

          <select
            value={filters.status}
            onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
            className={`px-4 py-3 rounded-lg border ${inputClass}`}
          >
            <option value="all">Todos los estados</option>
            <option value="active">Activos</option>
            <option value="inactive">Inactivos</option>
            <option value="trial">Trial</option>
          </select>

          <select
            value={filters.role}
            onChange={(e) => setFilters(prev => ({ ...prev, role: e.target.value }))}
            className={`px-4 py-3 rounded-lg border ${inputClass}`}
          >
            <option value="all">Todos los roles</option>
            <option value="admin">Admin</option>
            <option value="user">Usuario</option>
            <option value="premium">Premium</option>
            <option value="technician">Soporte</option>
            <option value="agent">Agente</option>
          </select>
        </div>

        {/* ✅ Estado de carga mejorado */}
        {loading ? (
          <div className={`${cardClass} rounded-xl p-12 text-center`}>
            <RefreshCw className="w-8 h-8 animate-spin mx-auto mb-4 text-blue-500" />
            <p className={`${mutedTextClass}`}>Cargando usuarios...</p>
          </div>
        ) : users.length === 0 ? (
          <div className={`${cardClass} rounded-xl p-12 text-center`}>
            <Users className="w-12 h-12 mx-auto mb-4 text-gray-400" />
            <h3 className={`text-lg font-semibold ${textClass} mb-2`}>
              No se encontraron usuarios
            </h3>
            <p className={`${mutedTextClass}`}>
              {searchQuery || filters.status !== 'all' || filters.role !== 'all' 
                ? 'Intenta ajustar los filtros de búsqueda' 
                : 'Comienza creando tu primer usuario'}
            </p>
          </div>
        ) : (
          <>
            {/* Table */}
            <div className={`rounded-xl border ${cardClass} overflow-hidden`}>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className={`${isDark ? 'bg-gray-700' : 'bg-gray-50'}`}>
                    <tr>
                      <th className="px-4 py-3">
                        <input
                          type="checkbox"
                          checked={selectedUsers.size === users.length && users.length > 0}
                          onChange={() => {
                            if (selectedUsers.size === users.length) {
                              setSelectedUsers(new Set())
                            } else {
                              setSelectedUsers(new Set(users.map(u => u.id)))
                            }
                          }}
                          className="rounded"
                        />
                      </th>
                      <th className={`px-4 py-3 text-left text-xs font-medium ${mutedTextClass} uppercase tracking-wider`}>
                        Usuario
                      </th>
                      <th className={`px-4 py-3 text-left text-xs font-medium ${mutedTextClass} uppercase tracking-wider`}>
                        Rol
                      </th>
                      <th className={`px-4 py-3 text-left text-xs font-medium ${mutedTextClass} uppercase tracking-wider`}>
                        Estado
                      </th>
                      <th className={`px-4 py-3 text-left text-xs font-medium ${mutedTextClass} uppercase tracking-wider`}>
                        Suscripción
                      </th>
                      <th className={`px-4 py-3 text-left text-xs font-medium ${mutedTextClass} uppercase tracking-wider`}>
                        Consultas
                      </th>
                      <th className={`px-4 py-3 text-left text-xs font-medium ${mutedTextClass} uppercase tracking-wider`}>
                        Creado
                      </th>
                      <th className={`px-4 py-3 text-center text-xs font-medium ${mutedTextClass} uppercase tracking-wider`}>
                        Acciones
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                    {users.map((user) => (
                      <tr key={user.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                        <td className="px-4 py-3">
                          <input
                            type="checkbox"
                            checked={selectedUsers.has(user.id)}
                            onChange={() => {
                              const newSelected = new Set(selectedUsers)
                              if (newSelected.has(user.id)) {
                                newSelected.delete(user.id)
                              } else {
                                newSelected.add(user.id)
                              }
                              setSelectedUsers(newSelected)
                            }}
                            className="rounded"
                          />
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white font-semibold">
                              {user.name?.[0]?.toUpperCase() || 'U'}
                            </div>
                            <div>
                              <div className={`font-medium ${textClass}`}>{user.name || 'Sin nombre'}</div>
                              <div className={`text-sm ${mutedTextClass}`}>{user.email}</div>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
                            user.role === 'admin' ? 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400' :
                            user.role === 'premium' ? 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400' :
                            'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-400'
                          }`}>
                            {user.role === 'admin' && <Crown className="w-3 h-3" />}
                            {user.role || 'user'}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          {/* ✅ Usar subscription_status de la BD */}
                          <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
                            user.status=== 'active' ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' :
                            user.status === 'suspended' ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400' :
                            'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
                          }`}>
                            {user.status === 'active' ? <CheckCircle className="w-3 h-3" /> : <XCircle className="w-3 h-3" />}
                            {user.status || 'inactive'}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <span className={`text-sm ${textClass}`}>
                            {user.subscription_type || 'free'}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <div className={`text-sm ${textClass}`}>
                            {user.queries_used || 0} / {user.queries_limit === 0 ? '∞' : user.queries_limit || 100}
                          </div>
                          <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-1.5 mt-1">
                            <div 
                              className="bg-blue-600 h-1.5 rounded-full" 
                              style={{ width: `${Math.min((user.queries_used / (user.queries_limit || 100)) * 100, 100)}%` }}
                            />
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <span className={`text-sm ${mutedTextClass}`}>
                            {new Date(user.created_at).toLocaleDateString('es-CL')}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center justify-center gap-2">
                            <button
                              onClick={() => {
                                setEditingUser(user)
                                setShowUserModal(true)
                              }}
                              className="p-2 hover:bg-blue-100 dark:hover:bg-blue-900/30 rounded-lg text-blue-600 dark:text-blue-400"
                              title="Editar usuario"
                            >
                              <Edit className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => {
                                setResetPasswordUser(user)
                                setShowPasswordModal(true)
                              }}
                              className="p-2 hover:bg-yellow-100 dark:hover:bg-yellow-900/30 rounded-lg text-yellow-600 dark:text-yellow-400"
                              title="Resetear contraseña"
                            >
                              <Key className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => {
                                if (confirm(`¿Eliminar a ${user.name}?`)) {
                                  handleUserAction('delete', user.id, null)
                                }
                              }}
                              className="p-2 hover:bg-red-100 dark:hover:bg-red-900/30 rounded-lg text-red-600 dark:text-red-400"
                              title="Eliminar usuario"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Pagination */}
            <div className="flex items-center justify-between">
              <div className={`text-sm ${mutedTextClass}`}>
                Mostrando {((pagination.currentPage - 1) * pagination.itemsPerPage) + 1} - {Math.min(pagination.currentPage * pagination.itemsPerPage, pagination.totalItems)} de {pagination.totalItems} usuarios
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => setPagination(prev => ({ ...prev, currentPage: prev.currentPage - 1 }))}
                  disabled={pagination.currentPage === 1}
                  className="flex items-center gap-2 px-4 py-2 rounded-lg border bg-white dark:bg-gray-800 disabled:opacity-50"
                >
                  <ChevronLeft className="w-4 h-4" />
                  Anterior
                </button>
                <button
                  onClick={() => setPagination(prev => ({ ...prev, currentPage: prev.currentPage + 1 }))}
                  disabled={pagination.currentPage >= pagination.totalPages}
                  className="flex items-center gap-2 px-4 py-2 rounded-lg border bg-white dark:bg-gray-800 disabled:opacity-50"
                >
                  Siguiente
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          </>
        )}

        {/* Modal Reset Password */}
        {showPasswordModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className={`${cardClass} rounded-xl p-6 w-full max-w-md`}>
              <div className="flex items-center justify-between mb-6">
                <h3 className={`text-xl font-bold ${textClass}`}>
                  Resetear Contraseña
                </h3>
                <button
                  onClick={() => {
                    setShowPasswordModal(false)
                    setResetPasswordUser(null)
                    setNewPassword('')
                  }}
                  className={`p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg`}
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <p className={`${mutedTextClass} mb-4`}>
                Usuario: <span className={`font-semibold ${textClass}`}>{resetPasswordUser?.name}</span>
              </p>

              <div className="mb-6">
                <label className={`block text-sm font-medium ${textClass} mb-2`}>
                  Nueva Contraseña
                </label>
                <input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Mínimo 8 caracteres"
                  minLength={8}
                  className={`w-full px-3 py-2 rounded-lg border ${inputClass}`}
                />
              </div>

              <div className="flex justify-end gap-3">
                <button
                  onClick={() => {
                    setShowPasswordModal(false)
                    setResetPasswordUser(null)
                    setNewPassword('')
                  }}
                  className="px-4 py-2 rounded-lg bg-gray-600 hover:bg-gray-700 text-white"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleResetPassword}
                  disabled={!newPassword || newPassword.length < 8}
                  className="flex items-center gap-2 px-4 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-50"
                >
                  <Key className="w-4 h-4" />
                  Actualizar Contraseña
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Modal Create/Edit User */}
        {showUserModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 overflow-y-auto">
            <div className={`${cardClass} rounded-xl p-6 w-full max-w-2xl my-8`}>
              <div className="flex items-center justify-between mb-6">
                <h3 className={`text-xl font-bold ${textClass}`}>
                  {editingUser ? 'Editar Usuario' : 'Nuevo Usuario'}
                </h3>
                <button
                  onClick={() => {
                    setShowUserModal(false)
                    setEditingUser(null)
                  }}
                  className={`p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg`}
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <form 
               onSubmit={(e) => {
                    e.preventDefault()
                    const formData = new FormData(e.currentTarget)
                    const data = {
                      name: formData.get('name'),
                      username: formData.get('username'),
                      email: formData.get('email'),
                      password: formData.get('password'),
                      phone: formData.get('phone'),
                      company: formData.get('company'),
                      country: formData.get('country'),
                      role: formData.get('role'),
                      status: formData.get('status'), // ✅ CORREGIDO
                      subscription_type: formData.get('subscription'),
                      queries_limit: parseInt(formData.get('queriesLimit'))
                    }
                    handleUserAction(editingUser ? 'update' : 'create', editingUser?.id, data)
                  }}
                className="space-y-4"
              >
                <div className="grid grid-cols-2 gap-4">
                  {/* Nombre */}
                  <div>
                    <label className={`block text-sm font-medium ${textClass} mb-2`}>
                      Nombre Completo *
                    </label>
                    <input
                      type="text"
                      name="name"
                      defaultValue={editingUser?.name}
                      required
                      className={`w-full px-3 py-2 rounded-lg border ${inputClass}`}
                      placeholder="Juan Pérez"
                    />
                  </div>
              
                  {/* Username */}
                  <div>
                    <label className={`block text-sm font-medium ${textClass} mb-2`}>
                      Username *
                    </label>
                    <input
                      type="text"
                      name="username"
                      defaultValue={editingUser?.username}
                      required
                      className={`w-full px-3 py-2 rounded-lg border ${inputClass}`}
                      placeholder="jperez"
                    />
                  </div>
              
                  {/* Email */}
                  <div>
                    <label className={`block text-sm font-medium ${textClass} mb-2`}>
                      Email *
                    </label>
                    <input
                      type="email"
                      name="email"
                      defaultValue={editingUser?.email}
                      required
                      className={`w-full px-3 py-2 rounded-lg border ${inputClass}`}
                      placeholder="usuario@empresa.com"
                    />
                  </div>
              
                  {/* Contraseña - Solo al crear */}
                  {!editingUser && (
                    <div>
                      <label className={`block text-sm font-medium ${textClass} mb-2`}>
                        Contraseña *
                      </label>
                      <input
                        type="password"
                        name="password"
                        required
                        minLength={8}
                        className={`w-full px-3 py-2 rounded-lg border ${inputClass}`}
                        placeholder="Mínimo 8 caracteres"
                      />
                    </div>
                  )}
              
                  {/* Teléfono */}
                  <div>
                    <label className={`block text-sm font-medium ${textClass} mb-2`}>
                      Teléfono
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      defaultValue={editingUser?.phone}
                      className={`w-full px-3 py-2 rounded-lg border ${inputClass}`}
                      placeholder="+56 9 1234 5678"
                    />
                  </div>
              
                  {/* Compañía */}
                  <div>
                    <label className={`block text-sm font-medium ${textClass} mb-2`}>
                      Compañía
                    </label>
                    <input
                      type="text"
                      name="company"
                      defaultValue={editingUser?.company}
                      className={`w-full px-3 py-2 rounded-lg border ${inputClass}`}
                      placeholder="Empresa ABC"
                    />
                  </div>
              
                  {/* País */}
                  <div>
                    <label className={`block text-sm font-medium ${textClass} mb-2`}>
                      País
                    </label>
                    <select
                      name="country"
                      defaultValue={editingUser?.country || 'CL'}
                      className={`w-full px-3 py-2 rounded-lg border ${inputClass}`}
                    >
                      <option value="CL">🇨🇱 Chile</option>
                      <option value="AR">🇦🇷 Argentina</option>
                      <option value="PE">🇵🇪 Perú</option>
                      <option value="MX">🇲🇽 México</option>
                      <option value="CO">🇨🇴 Colombia</option>
                      <option value="BR">🇧🇷 Brasil</option>
                      <option value="US">🇺🇸 Estados Unidos</option>
                      <option value="ES">🇪🇸 España</option>
                      <option value="OTHER">🌎 Otro</option>
                    </select>
                  </div>
              
                  {/* Rol */}
                  <div>
                    <label className={`block text-sm font-medium ${textClass} mb-2`}>
                      Rol *
                    </label>
                    <select
                      name="role"
                      defaultValue={editingUser?.role || 'user'}
                      className={`w-full px-3 py-2 rounded-lg border ${inputClass}`}
                    >
                      <option value="user">Usuario</option>
                      <option value="premium">Premium</option>
                      <option value="admin">Administrador</option>
                      <option value="technician">Soporte</option>
                      <option value="agent">Agente</option>
                    </select>
                  </div>
              
                  {/* Estado de Suscripción */}
                  <div>
                    <label className={`block text-sm font-medium ${textClass} mb-2`}>
                      Estado *
                    </label>
                    <select
                      name="status"
                      defaultValue={editingUser?.subscription_status || 'active'}
                      className={`w-full px-3 py-2 rounded-lg border ${inputClass}`}
                    >
                      <option value="active">Activo</option>
                      <option value="inactive">Inactivo</option>
                      <option value="trial">Trial</option>
                      <option value="suspended">Suspendido</option>
                    </select>
                  </div>
              
                  {/* Plan de Suscripción */}
                  <div>
                    <label className={`block text-sm font-medium ${textClass} mb-2`}>
                      Plan de Suscripción *
                    </label>
                    <select
                      name="subscription"
                      defaultValue={editingUser?.subscription_type || 'free'}
                      className={`w-full px-3 py-2 rounded-lg border ${inputClass}`}
                    >
                      <option value="free">Gratis</option>
                      <option value="basic">Básico</option>
                      <option value="professional">Profesional</option>
                      <option value="enterprise">Enterprise</option>
                    </select>
                  </div>
              
                  {/* Límite de Consultas */}
                  <div>
                    <label className={`block text-sm font-medium ${textClass} mb-2`}>
                      Límite de Consultas *
                    </label>
                    <input
                      type="number"
                      name="queriesLimit"
                      defaultValue={editingUser?.queries_limit || 100}
                      min={0}
                      className={`w-full px-3 py-2 rounded-lg border ${inputClass}`}
                      placeholder="100"
                    />
                    <p className={`text-xs ${mutedTextClass} mt-1`}>
                      0 = ilimitado
                    </p>
                  </div>
                </div>
              
                {/* Mensaje de cambio de contraseña */}
                {editingUser && (
                  <div className={`mb-6 p-3 rounded-lg ${isDark ? 'bg-blue-900/20 border-blue-500/30' : 'bg-blue-50 border-blue-200'} border`}>
                    <p className={`text-sm ${isDark ? 'text-blue-300' : 'text-blue-700'}`}>
                      💡 La contraseña no se modifica en la edición. Para cambiarla, usa la función "Resetear contraseña".
                    </p>
                  </div>
                )}
              
                <div className="flex justify-end gap-3">
                  <button
                    type="button"
                    onClick={() => setShowUserModal(false)}
                    className="px-4 py-2 rounded-lg bg-gray-600 hover:bg-gray-700 text-white"
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    className="flex items-center gap-2 px-4 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white"
                  >
                    <Save className="w-4 h-4" />
                    {editingUser ? 'Actualizar' : 'Crear Usuario'}
                  </button>
                </div>
              </form>
              
            </div>
          </div>
        )}
      </div>
    </div>
  )
}